import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMerchantCamBookings } from "../../../../features/booklet/bookletThunks";
import { createCashPayment } from "../../../../features/payment/paymentThunks";
import PaymentOptionModal from "../pyment/PaymentOption";
import PaymentQRModal from "../pyment/PaymentQRModal";
import useOnlinePayment from "../../../../hooks/useOnlinePayment";
import useCashPayment from "../../../../hooks/useCashPayment";

const statusColor = {
  unpaid: "#dc3545",
  partial: "#fd7e14",
  paid: "#198754",
  failed: "#6c757d",
};

const CamOrders = () => {
  const dispatch = useDispatch();
  const { myBookings, myBookingsLoading } = useSelector((state) => state.booklet);

  const [selectedBooking, setSelectedBooking] = useState(null);

  // Amount picker state (step 1: choose how much to pay)
  const [amountPickerOpen, setAmountPickerOpen] = useState(false);
  const [customPayAmount, setCustomPayAmount] = useState("");
  const [amountPickError, setAmountPickError] = useState("");

  // Payment method modal state (step 2: choose payment method)
  const [paymentOpen, setPaymentOpen] = useState(false);

  const { startOnlinePayment } = useOnlinePayment();
  const { cashPaymentData, status, payment_status, closePayment } = useCashPayment({ autoReload: true });

  useEffect(() => {
    dispatch(fetchMerchantCamBookings());
  }, [dispatch]);

  const getRemaining = (b) => b.pendingAmount ?? (b.totalAmount - (b.paidAmount || 0));

  const getMinPayment = (b) => {
    const remaining = getRemaining(b);
    return Math.ceil(Math.min(remaining, b.totalAmount * 0.10) * 100) / 100;
  };

  const handlePayRemainingClick = (booking) => {
    const remaining = getRemaining(booking);
    setSelectedBooking(booking);
    setCustomPayAmount(remaining.toFixed(2));
    setAmountPickError("");
    setAmountPickerOpen(true);
  };

  const handleAmountConfirm = () => {
    if (!selectedBooking) return;
    const amt = parseFloat(customPayAmount);
    const remaining = getRemaining(selectedBooking);
    const minPay = getMinPayment(selectedBooking);
    if (isNaN(amt) || amt < minPay) {
      setAmountPickError(`Minimum payment is ₹${minPay.toFixed(2)} (10% of booking total)`);
      return;
    }
    if (amt > remaining) {
      setAmountPickError(`Amount cannot exceed remaining balance of ₹${remaining.toFixed(2)}`);
      return;
    }
    setAmountPickerOpen(false);
    setPaymentOpen(true);
  };

  const handlePaymentSelect = async (method) => {
    if (!selectedBooking) return;
    const payAmount = parseFloat(customPayAmount);
    const remaining = getRemaining(selectedBooking);
    const isPartial = payAmount < remaining;

    if (method === "cash") {
      try {
        const result = await dispatch(
          createCashPayment({
            bookingId: selectedBooking.bookingId,
            amount: payAmount,
            paymentFor: "booklet",
            isPartialPayment: isPartial,
          })
        );
        if (!createCashPayment.fulfilled.match(result)) {
          alert("Failed to create cash payment");
          return;
        }
        setPaymentOpen(false);
      } catch (err) {
        alert("Something went wrong. Please try again.");
      }
      return;
    }

    if (method === "online") {
      setPaymentOpen(false);
      startOnlinePayment({
        bookingId: selectedBooking.bookingId,
        amount: payAmount,
        paymentFor: "booklet",
        isPartialPayment: isPartial,
        onSuccess: () => {
          setTimeout(() => dispatch(fetchMerchantCamBookings()), 1500);
        },
        onFailure: (msg) => alert(msg || "Payment failed. Please try again."),
      });
    }
  };

  if (myBookingsLoading) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <div className="bk-spinner" style={{ margin: "0 auto 12px" }} />
        <p>Loading your CAM bookings...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px" }}>
      <h2 style={{ fontWeight: 700, marginBottom: 8 }}>My CAM Bookings</h2>
      <p style={{ color: "#6b7280", marginBottom: 24 }}>
        Collective Advertising Module — view payment status and pay outstanding balance.
      </p>

      {myBookings.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>
          No CAM bookings found.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {myBookings.map((b) => {
            const pending = getRemaining(b);
            const hasPending = pending > 0.005;

            return (
              <div
                key={b.bookingId}
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                }}
              >
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{b.bookletName || "Booklet"}</span>
                    <span
                      style={{
                        marginLeft: 10,
                        fontSize: 11,
                        fontWeight: 700,
                        background: "#eef2ff",
                        color: "#463B7F",
                        padding: "2px 8px",
                        borderRadius: 5,
                        textTransform: "uppercase",
                      }}
                    >
                      CAM
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#fff",
                      background: statusColor[b.payment_status] || "#6c757d",
                      padding: "3px 10px",
                      borderRadius: 20,
                      textTransform: "uppercase",
                    }}
                  >
                    {b.payment_status}
                  </span>
                </div>

                {/* Booking ID */}
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                  Booking ID: <code>{b.bookingId}</code>
                </p>

                {/* Amount Breakdown */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 12,
                    background: "#f9fafb",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>Total Amount</div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>₹{b.totalAmount?.toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#198754" }}>Paid Amount</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#198754" }}>
                      ₹{(b.paidAmount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: hasPending ? "#dc3545" : "#6b7280" }}>
                      Pending Amount
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: hasPending ? "#dc3545" : "#198754",
                      }}
                    >
                      ₹{pending.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Slots & Status */}
                <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                  <span>Slots: <strong>{b.totalSlots}</strong></span>
                  <span>Booking Status: <strong>{b.status}</strong></span>
                  <span>Booked: <strong>{new Date(b.bookedAt).toLocaleDateString()}</strong></span>
                </div>

                {/* Pay Remaining Button */}
                {hasPending && (
                  <button
                    style={{
                      background: "#463B7F",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 20px",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                    onClick={() => handlePayRemainingClick(b)}
                  >
                    Pay Remaining ₹{pending.toFixed(2)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Amount Picker Modal */}
      {amountPickerOpen && selectedBooking && (() => {
        const remaining = getRemaining(selectedBooking);
        const minPay = getMinPayment(selectedBooking);
        return (
          <div
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
              zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff", borderRadius: 12, padding: 28,
                width: "100%", maxWidth: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              }}
            >
              <h5 style={{ fontWeight: 700, marginBottom: 4 }}>Choose Payment Amount</h5>
              <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
                Remaining balance: <strong style={{ color: "#dc3545" }}>₹{remaining.toFixed(2)}</strong>
              </p>

              <div style={{ marginBottom: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>
                  Amount to Pay Now (₹{minPay.toFixed(2)} – ₹{remaining.toFixed(2)})
                </label>
                <input
                  type="number"
                  value={customPayAmount}
                  onChange={(e) => { setCustomPayAmount(e.target.value); setAmountPickError(""); }}
                  min={minPay}
                  max={remaining}
                  step="0.01"
                  style={{
                    width: "100%", padding: "8px 10px", borderRadius: 6,
                    border: amountPickError ? "1px solid #dc3545" : "1px solid #d1d5db",
                    fontSize: 15, outline: "none",
                  }}
                />
                {amountPickError && (
                  <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>{amountPickError}</div>
                )}
                {!amountPickError && parseFloat(customPayAmount) < remaining && parseFloat(customPayAmount) > 0 && (
                  <div style={{ color: "#fd7e14", fontSize: 12, marginTop: 4 }}>
                    Pending after this: ₹{Math.max(0, remaining - parseFloat(customPayAmount)).toFixed(2)}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  onClick={() => setAmountPickerOpen(false)}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #d1d5db",
                    background: "#f9fafb", cursor: "pointer", fontWeight: 500,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAmountConfirm}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                    background: "#463B7F", color: "#fff", cursor: "pointer", fontWeight: 600,
                  }}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <PaymentOptionModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        onSelect={handlePaymentSelect}
      />

      <PaymentQRModal
        open={Boolean(cashPaymentData)}
        data={cashPaymentData}
        status={status}
        payment_status={payment_status}
        onClose={closePayment}
      />

      <style>{`
        .bk-spinner {
          width: 36px;
          height: 36px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #463B7F;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default CamOrders;
